import { Edit, Trash2, GripVertical, Zap, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import { useUpdateRule, useDeleteRule } from '@/hooks/useRules';

function isGroup(item) {
  return item && item.match_type && Array.isArray(item.conditions);
}

function valueAction(type) {
  return type === 'set_category' || type === 'set_priority';
}

function renderConditions(conditions, matchType, limit = 3) {
  const matchLabel = matchType === 'any' ? 'or' : 'and';
  const itemsToShow = conditions.slice(0, limit);
  const remaining = conditions.length - limit;
  const elements = [];

  itemsToShow.forEach((item, idx) => {
    if (idx > 0) {
      elements.push(
        <span key={`sep-${idx}`} className="text-xs font-medium text-muted-foreground mx-0.5">
          {matchLabel}
        </span>
      );
    }

    if (isGroup(item)) {
      elements.push(
        <div key={`item-${idx}`} className="p-2 border rounded-md bg-muted/30">
          {renderConditions(item.conditions, item.match_type)}
        </div>
      );
    } else {
      elements.push(
        <Badge key={`item-${idx}`} variant="outline" className="text-xs font-mono">
          <span className="font-semibold">{item.field}</span>{' '}
          <span className="text-muted-foreground">{item.operator}</span>{' '}
          <span className="text-primary">{item.value}</span>
        </Badge>
      );
    }
  });

  if (remaining > 0) {
    if (itemsToShow.length > 0) {
      elements.push(
        <span key="sep-more" className="text-xs font-medium text-muted-foreground mx-0.5">
          {matchLabel}
        </span>
      );
    }
    elements.push(
      <Badge key="more" variant="outline" className="text-xs">
        +{remaining} more
      </Badge>
    );
  }

  return elements;
}

export default function RuleCard({ rule, onEdit, dragHandleProps }) {
  const updateMutation = useUpdateRule();
  const deleteMutation = useDeleteRule();

  const handleToggle = () => {
    updateMutation.mutate({
      id: rule.id,
      data: { enabled: !rule.enabled },
    });
  };

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete rule "${rule.name}"?`)) {
      deleteMutation.mutate(rule.id);
    }
  };

  const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
  const actions = Array.isArray(rule.actions) ? rule.actions : [];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Drag Handle */}
          {dragHandleProps && (
            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing pt-1">
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
          )}

          {/* Rule Icon */}
          <div className={`flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${
            rule.type === 'ai' ? 'bg-purple-100 dark:bg-purple-900' : 'bg-blue-100 dark:bg-blue-900'
          }`}>
            {rule.type === 'ai' ? (
              <Zap className={`h-5 w-5 ${rule.type === 'ai' ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'}`} />
            ) : (
              <Filter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>

          {/* Rule Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{rule.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Priority: {rule.priority || 0}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant={rule.type === 'ai' ? 'default' : 'secondary'}>
                  {rule.type === 'ai' ? 'AI Rule' : 'Static Rule'}
                </Badge>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={handleToggle}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Conditions */}
            {conditions.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Conditions:</p>
                <div className="flex flex-wrap items-center gap-1">
                  {renderConditions(conditions, rule.match_type || 'all')}
                </div>
              </div>
            )}

            {/* Actions */}
            {actions.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Actions:</p>
                <div className="flex flex-wrap gap-1">
                  {actions.slice(0, 3).map((action, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {action.type}
                      {valueAction(action.type) && action.value ? `: ${action.value}` : ''}
                    </Badge>
                  ))}
                  {actions.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{actions.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(rule)}
            >
              <Edit className="h-4 w-4" />
            </Button>

            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
